import React from 'react';
import { isNil, isEmpty, padStart, get, memoize, isEqual } from 'lodash';
import { detect } from 'detect-browser';
import Cookies from 'universal-cookie';
import { FormattedMessage } from 'react-intl';
import qs from 'qs';
import {
  imagePatternDivider,
  highAvailabilityServerAddress,
  suggestedAmountGrowthFactor,
  whitelabelSubdomainExceptions,
  isFudrManagedDomain,  // FUDR-13245
  fudrManagedDomains,   // FUDR-13245: needed for suffix-stripping in deriveSubdomain
  IPV4_REGEX,           // FUDR-13245: shared constant, avoids inline regex duplication
  operatingPlatformsList,
  onlinePaymentTypesConfig,
  greetingTimeSalutationsMap,
  timeOfDayStrings,
  enableProactiveTokenRefresh,
} from '../constants/globals';
import { serverErrorCodes } from '../constants/globals';
import { constants } from '../constants/configuration';
import { layoutConfiguration } from '../constants/layoutConfiguration';
import routes from '../routes';
import DeviceDetector from 'device-detector-js';
import history from '../helpers/history';
import rewardsRoutes from '../routes/rewards';
import orderingRoutes from '../routes/ordering';
import { pointUnits, nativeEventType } from '../containers/Rewards/config';
import resourcesConfig from '../resources';
import {
  getCurrencyDisplaySymbol,
} from './currencyDisplay';

const cookies = new Cookies();

// helper methods
export const buildSearchWithView = (query, viewValue) =>
  qs.stringify(
    {
      ...query,
      ...(viewValue ? { view: viewValue } : { view: undefined }),
    },
    { addQueryPrefix: true, skipNulls: true },
  );

export const replaceAt = (string = '', index, replacement) => {
  return string.substr(0, index) + replacement + string.substr(index + replacement.length);
};

/**
 * Possessive brand label for copy like "{brand}'s Privacy Policy".
 * Skips appending ’s when the name already ends in 's / ’s / s' / s’.
 */
export const formatBrandPossessive = (brandName = '') => {
  const name = String(brandName || '').trim();
  if (!name) {
    return '';
  }
  if (/['’]s$/i.test(name) || /s['’]$/i.test(name)) {
    return name;
  }
  return `${name}’s`;
};

export const getPageWidth = () => {
  return Math.max(
    document.body.scrollWidth,
    document.documentElement.scrollWidth,
    document.body.offsetWidth,
    document.documentElement.offsetWidth,
    document.documentElement.clientWidth,
  );
};



export const formatMoney = (amount, currencyCode) => {
  const numericAmount = Number(amount);
  const resolvedAmount =
    Number.isNaN(numericAmount) || !Number.isFinite(numericAmount) ? 0 : numericAmount;

  const symbol = getCurrencyDisplaySymbol(currencyCode);
  return symbol ? `${symbol}\u200E${resolvedAmount}` : String(resolvedAmount);
};

/**
 * react-intl values for money lines when brand CMS may still use legacy placeholders
 * (`{currencySymbol}{amount}`, `{currency}{amount}`) alongside `{formattedAmount}`.
 * @param {number|string} amount — raw numeric amount (not pre-formatted)
 */
export const intlMoneyMessageValues = (amount, currencyCode = '') => {
  const numericAmount = Number(amount);
  const resolvedAmount =
    Number.isNaN(numericAmount) || !Number.isFinite(numericAmount) ? 0 : numericAmount;
  const formattedAmount = formatMoney(resolvedAmount, currencyCode);
  return {
    formattedAmount,
    currencySymbol: getCurrencySymbol(currencyCode),
    currency: getCurrencySymbol(currencyCode),
    amount: String(resolvedAmount),
  };
};

/**
 * Wallet cart lines use `{formattedBalance}`; brand CMS may still use `{balance}` or
 * `{currencySymbol}{amount}` alongside {@link intlMoneyMessageValues}.
 */
export const intlWalletBalanceMessageValues = (balance, currencyCode = '') => {
  const formattedBalance = formatMoney(balance, currencyCode);
  return {
    formattedBalance,
    balance: formattedBalance,
    ...intlMoneyMessageValues(balance, currencyCode),
  };
};

/** Currency symbol from supportedCurrencies masterData (displayCode). */
export const getCurrencySymbol = (currencyCode) => {
  if (typeof currencyCode !== 'string' || !currencyCode) return '';
  return getCurrencyDisplaySymbol(currencyCode);
};

/** @deprecated Prefer {@link formatMoney} */
export const getFormattedPrice = (price, currencyCode = '') =>
  formatMoney(roundOff(price || 0, 2), currencyCode);

export const drawLine = (canvas, begin, end, color) => {
  canvas.beginPath();
  canvas.moveTo(begin.x, begin.y);
  canvas.lineTo(end.x, end.y);
  canvas.lineWidth = 4;
  canvas.strokeStyle = color;
  canvas.stroke();
};

// get color based on index
// TODO: Clean this function up if it's no longer being used
export const getColorIndex = (index) => {
  // return index % 2 ? '#f5f5f5' : '#fff';
};

export const scrollElementIntoView = (id, top) => {
  const el = document.getElementById(id);
  !isNil(el) &&
    el.parentElement.scrollTo({
      ...(top !== undefined && { top }),
      left: el.offsetLeft,
      behavior: 'smooth',
    });
};

export const degreesToRadians = (degrees) => {
  return (degrees * Math.PI) / 180;
};

export const radiansToDegrees = (radians) => {
  return radians * (180 / Math.PI);
};

export const fetchGeoLocation = async (geoLocationCallback, error) => {
  await navigator.geolocation.getCurrentPosition((position) => {
    geoLocationCallback(position);
  }, error);
};

/**
 *
 * @param {Float} latitude1
 * @param {Float} longitude1
 * @param {Float} latitude2
 * @param {Float} longitude2
 * @returns distance between the coordinates
 */
export const checkDistanceBetweenCoordinates = (
  { latitude1, longitude1, latitude2, longitude2 },
  roundOff = true,
) => {
  if (latitude1 == latitude2 && longitude1 == longitude2) {
    return 0;
  } else {
    const theta = longitude1 - longitude2;
    let distance =
      Math.sin(degreesToRadians(latitude1)) * Math.sin(degreesToRadians(latitude2)) +
      Math.cos(degreesToRadians(latitude1)) *
      Math.cos(degreesToRadians(latitude2)) *
      Math.cos(degreesToRadians(theta));
    distance = Math.acos(distance);
    distance = radiansToDegrees(distance);
    distance = distance * 60 * 1.1515;
    distance = distance * 1.609344; // km factor
    return roundOff ? Math.round(distance, 1) : distance;
  }
};

export const getTableLabel = (type, tableTitle, tableLabel) => {
  let label = {
    SP: tableLabel,
    DI: !isEmpty(tableLabel)
      ? `${!isEmpty(tableTitle) ? tableTitle : constants.tableType.table} ${tableLabel}`
      : '',
    DT: tableLabel,
    RO: tableLabel,
    DL: tableLabel,
  };
  return label[type];
};

export const dineInModes = ['DI', 'RO'];

export const getCurrentRestaurantCode = ({ restaurantCode, tableNumber }) => {
  let currentRestaurantCode = '';
  if (!isEmpty(restaurantCode) && !isEmpty(tableNumber)) {
    currentRestaurantCode = `/${routes.containers.restaurant}/${restaurantCode + padStart(tableNumber, 3, '0')
      }`;
  } else {
    currentRestaurantCode = `/${routes.containers.scan}`;
  }
  return currentRestaurantCode;
};

/* Credits: To Title Case © 2018 David Gouch | https://github.com/gouch/to-title-case */
// Converts a string to Title Case
// eslint-disable-next-line no-extend-native
export const toTitleCase = (string) => {
  if (string == null || string === '') {
    return '';
  }

  const input = typeof string === 'string' ? string : String(string);
  const smallWords = /^(a|an|and|as|at|but|by|en|for|if|in|nor|of|on|or|per|the|to|v.?|vs.?|via)$/i;
  const alphanumericPattern = /([A-Za-z0-9\u00C0-\u00FF])/;
  const wordSeparators = /([ :–—-])/;

  return input
    .split(wordSeparators)
    .map(function (current, index, array) {
      if (
        /* Check for small words */
        current.search(smallWords) > -1 &&
        /* Skip first and last word */
        index !== 0 &&
        index !== array.length - 1 &&
        /* Ignore title end and subtitle start */
        array[index - 3] !== ':' &&
        array[index + 1] !== ':' &&
        /* Ignore small words that start a hyphenated phrase */
        (array[index + 1] !== '-' || (array[index - 1] === '-' && array[index + 1] === '-'))
      ) {
        return current.toLowerCase();
      }

      /* Ignore intentional capitalization */
      if (current.substr(1).search(/[A-Z]|\../) > -1) {
        return current;
      }

      /* Ignore URLs */
      if (array[index + 1] === ':' && array[index + 2] !== '') {
        return current;
      }

      /* Capitalize the first letter */
      return current.replace(alphanumericPattern, function (match) {
        return match.toUpperCase();
      });
    })
    .join('');
};

// Format address lines to comma separated title case text (dedupe repeated segments).
export const formatAddress = ({
  addressLine1,
  landmark,
  localityName,
  localitySecondaryText,
} = {}) => {
  const seen = new Set();
  const parts = [];
  [addressLine1, landmark, localityName, localitySecondaryText].forEach((value) => {
    if (value == null || value === '') {
      return;
    }
    String(value)
      .split(',')
      .map((segment) => segment.trim())
      .filter(Boolean)
      .forEach((segment) => {
        const key = segment.toLowerCase();
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        parts.push(segment);
      });
  });
  return toTitleCase(parts.join(', '));
};

/**
 *
 * @param {Number} index Index of the item which contains renderable image
 * @param {Number} renderableItemsCount Total count of renderable items (containing image(s))
 */
export const renderImageType = (index, renderableItemsCount) => {
  const remainder = index % imagePatternDivider;
  switch (remainder) {
    case 0:
      return 'bg'; // Zeroth index image should always be big
    case 1:
      if (renderableItemsCount < imagePatternDivider) {
        // If items count is less than pattern limiter, then return bg
        return 'bg';
      } else {
        // Else if it's the last index of the array, return bg, else sm
        return index === renderableItemsCount - 1 ? 'bg' : 'sm';
      }
    default:
      return 'sm';
  }
};

export const renderCount = (count) => {
  switch (count) {
    case 1:
      return `${count}st`;
    case 2:
      return `${count}nd`;
    case 3:
      return `${count}rd`;
    default:
      return `${count}th`;
  }
};

/**
 * function to calculate total amount sum
 * @param {*} collection
 * @param {*} decimal
 */
export const getTotalAmountSum = (collection, decimal) => {
  let roundOff = Math.pow(10, decimal);
  let sum = collection.reduce(
    (accumulator, currentValue) => accumulator + currentValue.totalAmount,
    0,
  );
  return Math.round(sum * roundOff) / roundOff;
};

/**
 * Returns a boolean indicating whether the app visitor is logged in or not.
 */
export const isUserLoggedIn = () => {
  const getParsedItem = (key) => {
    const item = localStorage.getItem(key);
    if (!item) return null; // Safety check: returns null if key doesn't exist

    try {
      return JSON.parse(item);
    } catch (e) {
      console.error(`Error parsing localStorage key "${key}":`, e);
      return null; // Return null if the JSON is corrupted
    }
  };

  const loginData = getParsedItem('GuestLoginData');
  const brandGuestData = getParsedItem('BrandGuestData');

  return !isEmpty(loginData) || !isEmpty(brandGuestData);
};

/**
 * Checks if the user-agent is online by using Navigator API and pinging a highly available server on the internet
 */
export const checkInternetConnectivity = () => {
  if (!navigator.onLine) {
    // This indicates that the user-agent is definitely offline
    const userOnlinePromise = new Promise((resolve, reject) => {
      resolve(false);
    });
    return userOnlinePromise;
  } else {
    // Try pinging a highly available server to double check if the user-agent is able to access the internet
    return pingServer(highAvailabilityServerAddress).then((isAvailable) => isAvailable);
  }
};

/**
 * Ping a server existing at the passed server address
 * @param {String} serverAddress A server url or dns
 * @returns {Promise} Resolution of promise returns a boolean indicating if the hit on the server was successful
 */
export const pingServer = async (serverAddress) => {
  if (!serverAddress) {
    return false;
  }

  try {
    await fetch(serverAddress, { mode: 'no-cors' }); // Fetch trial, if successful, will continue here instead of jumping to catch
    return true;
  } catch (error) {
    return false;
  }
};

// Detects if the user agent is in standalone mode
export const isInStandaloneMode = () =>
  'standalone' in window.navigator && window.navigator.standalone;

/**
 * Returns an object containing browser name and version along with additional info about the user agent running the webapp
 * @returns {Object} Browser info
 */
export const detectWebAppBrowser = () => detect();

export const detectWebAppOS = () => {
  const browser = detectWebAppBrowser();
  return browser ? browser.os : null;
};

/**
 * FUDR-13245 — Tenant identifier for /appStartup and other “resolve tenant by host” calls only.
 *
 * Flow:
 * 1) Fudr-managed host (e.g. brand.fudr.in): behaviour stays equivalent for normal single-level
 *    tenants — return the tenant key used today (suffix-stripped, same idea as backend).
 * 2) Custom domain (e.g. app.brand.com): return the full hostname so the backend can map it to
 *    the internal tenant; do not use this string as brandId in /brands/* paths.
 * 3) After appStartup succeeds, use response.brandId (Redux: common.appStartupInfo.brandId) for
 *    every API that expects the canonical brand code.
 *
 * prodServer sets targetHostName cookie to the request hostname; this reads that or location.hostname.
 *
 * @param {string} hostname
 * @returns {string|false}
 */
const deriveSubdomain = (hostname) => {
  if (!hostname) return false;
  // Bare label with no dot (e.g. "localhost") — not a valid fudr or custom domain.
  if (!hostname.includes('.')) return false;

  // Normalise to lowercase so the suffix-find below matches fudrManagedDomains entries
  // (which are already lowercase). Without this, a mixed-case hostname passes
  // isFudrManagedDomain() (which lowercases internally) but the subsequent find()
  // returns undefined, crashing on fudrSuffix.length.
  hostname = hostname.toLowerCase();

  if (isFudrManagedDomain(hostname)) {
    // Suffix-strip to extract the tenant identifier — mirrors the backend fix in
    // resolveByHostname(). split('.')[0] is fragile: for "sub.brand.fudr.in" it
    // returns "sub" instead of "brand". Stripping the known suffix gives the full
    // subdomain portion ("sub.brand"), and we can then reject multi-level paths.
    // Defensive guard: isFudrManagedDomain() should imply a suffix match, but this prevents
    // TypeError on fudrSuffix.length if future config/parsing drift breaks that assumption.
    const fudrSuffix = fudrManagedDomains.find((d) => hostname.endsWith('.' + d));
    if (!fudrSuffix) return false;
    const subdomainPart = hostname.slice(0, hostname.length - fudrSuffix.length - 1);
    // Multi-level subdomain (e.g. "sub.brand") is not a valid single-tenant key.
    if (subdomainPart.includes('.')) return false;
    return whitelabelSubdomainExceptions.includes(subdomainPart) ? false : subdomainPart;
  }

  // Custom domain path (not a fudr-managed host).
  // Exceptions list is intentionally skipped — a tenant may legitimately serve from
  // www.brand.com, app.brand.com, etc.
  // isValidDomainString() is not called here because:
  //   • targetHostName cookie is set by prodServer.js after its own validation
  //   • window.location.hostname is always a syntactically valid host per browser spec
  //   • the backend resolveByHostname() will reject unrecognised hostnames anyway
  // Reject IPv4 addresses — they can never be registered as custom domains.
  if (IPV4_REGEX.test(hostname)) return false;
  return hostname;
};

/** @returns {boolean|string} Tenant id for startup resolution, or false if not whitelabel context */
export const extractWhitelabelSubdomain = () => {
  const hostname = cookies.get('targetHostName') || window.location.hostname;
  if (isEmpty(hostname)) return false;
  return deriveSubdomain(hostname);
};

// As long as the callback continues to be invoked, it will not be triggered
export const debounce = (func, delay = 200, immediate = false) => {
  let timeout;

  return function () {
    const context = this,
      args = arguments;
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };

    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, delay);
    if (callNow) func.apply(context, args);
  };
};

// As long as the callback continues to be invoked, raise on every interval
export const throttle = (func, interval = 200, immediate = false) => {
  let timeout = null;
  let initialCall = true;

  return function () {
    const context = this,
      args = arguments;
    const callNow = immediate && initialCall;
    const later = () => {
      func.apply(context, args);
      timeout = null;
    };

    if (callNow) {
      initialCall = false;
      later();
    }

    if (!timeout) {
      timeout = setTimeout(later, interval);
    }
  };
};

/**
 * Returns a restaurant entity's social profile link by social platform's app code
 *
 * @param {string} socialAppCode Social platform's app code
 * @param {array} restaurantAdditionalParams Additional parameters object in restaurant entity's config data
 * @return {string} restaurantEntitySocialLink Restaurant entity's social profile link
 */
export const getRestaurantEntitySocialLinkByCode = (
  socialAppCode = '',
  restaurantAdditionalParams = [],
) => {
  const matchingAdditionalParamObj = restaurantAdditionalParams.find(
    (additionalParam) => get(additionalParam, 'code') === socialAppCode,
  );
  const restaurantEntitySocialLink = get(matchingAdditionalParamObj, 'value', '');
  return restaurantEntitySocialLink || '';
};

/**
 * Sorts an object by its keys based on a compare function (optional)
 * @param {object} unsortedObject Unsorted object
 * @param {Function} [compareFunction] Optional callback which provides a compare function to serve as the basis for sort, default sort based on ASCII comparator
 *
 * @return {object} sortedObject Sorted object
 */
export const sortObjectByKeysUsingComparator = (unsortedObject, compareFunction) => {
  let sortedObject = {};
  Object.keys(unsortedObject)
    .sort(compareFunction)
    .forEach(function (key) {
      sortedObject[key] = unsortedObject[key];
    });

  return sortedObject;
};

/**
 * return origin from string from current url
 * if url has no query string than get origin from document.referrer
 * but in some case we get empty in referrer of document in that case default origin will be 'ext'
 */
export const getURLOrigin = () => {
  const queryString = get(window, 'location.search', '');
  const searchParams = new URLSearchParams(queryString);
  const defaultQueryString = !!document.referrer ? document.referrer : 'ext';

  return searchParams.has('origin') ? searchParams.get('origin') : defaultQueryString;
};

/**
 * Registers event listerners to start tracking user activity on the webapp and executes a callback delimited by a throttle
 * @param {Function} func Callback to throttle-execute when tracking user activity
 */
export const activateUserActivityTracker = (func, userActivityTrackingThrottleTime) => {
  window.addEventListener('load', debounce(func, userActivityTrackingThrottleTime, true));
  window.addEventListener('mousemove', debounce(func, userActivityTrackingThrottleTime, true));
  window.addEventListener('scroll', debounce(func, userActivityTrackingThrottleTime, true));
  window.addEventListener('keydown', debounce(func, userActivityTrackingThrottleTime, true));
  window.addEventListener('touchstart', debounce(func, userActivityTrackingThrottleTime, true));
};

/**
 * Rounds off the given value upto 'precision' places of decimal
 *
 * @param {Number} value Numeric value to be rounded off
 * @param {Number} precision Places after dot to round off upto
 */
export const roundOff = (value, precision = 0) => {
  let precisionMultiplier = Math.pow(10, precision);
  return Math.round((value + Number.EPSILON) * precisionMultiplier) / precisionMultiplier; // Number.EPSILON offset helps round off figures ending in 5 like 0.05, 1.145, uniformly across all browsers
};

/**
 * To check the passed element is in viewport of device or not
 *
 * @param {element} object DOM object of element
 */
export const isElementInViewPort = ({ elements, headerHeight }) => {
  // provides the element’s position and its relative position to the viewport.
  const elementKeys = Object.keys(elements);
  let positionOfLastElementFromTop = {};
  elementKeys.forEach((key) => {
    const elementChildren = elements[key] && get(elements[key], 'current.children', []);
    //getBoundingClientRect() will give element top,bottom,height,width value
    if (elementChildren.length > 0) {
      const lastChild = elementChildren[elementChildren.length - 1].getBoundingClientRect();
      if (lastChild.top - headerHeight > 0) {
        positionOfLastElementFromTop[lastChild.top] = key;
      }
    }
  });
  let minPositionFromTop = Math.min(...Object.keys(positionOfLastElementFromTop));
  return positionOfLastElementFromTop[minPositionFromTop];
};

/**
 * Scroll element to top or  bottom
 *
 * @param {id} string id of element
 * @param {top} boolean true for top and false for bottom
 */
export const scrollIntoView = ({ id, top = true }) => {
  const element = document.getElementById([id]);
  !isNil(element) && element.scrollIntoView(top);
};

export const getSuggestedAmounts = memoize((referenceAmount, count = 4) => {
  let suggestedAmounts = [];

  for (let i = 0; i < count; i++) {
    const value = i !== 0 ? suggestedAmounts[i - 1] * suggestedAmountGrowthFactor : referenceAmount;
    suggestedAmounts.push(value);
  }

  return suggestedAmounts;
});

export const generateUUID = () => {
  let basex = 16;
  let d = new Date().getTime();
  if (window.performance && typeof window.performance.now === 'function') {
    d += performance.now(); //use high-precision timer if available
  }
  let uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = (d + Math.random() * basex) % basex | 0;
    d = Math.floor(d / basex);
    let sr = 0x3;
    let srx = r & sr;
    let srx1 = 0x8;
    return (c === 'x' ? r : srx | srx1).toString(basex);
  });
  return uuid;
};

export const scaleToWrapperWidth = (targetElement, wrapperElement) => {
  if (wrapperElement) {
    wrapperElement.style.width = '100%';
    wrapperElement.style.position = 'relative';
  }
  const targetElementWidth = targetElement && targetElement.offsetWidth;
  const wrapperElementWidth = wrapperElement && wrapperElement.offsetWidth;
  if (wrapperElementWidth && targetElementWidth) {
    const scaleFactor = wrapperElementWidth / targetElementWidth;
    targetElement.style.transform = `scale(${scaleFactor})`;
    wrapperElement.style.display = 'contents';
  }
};

export const isIosWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(
  navigator.userAgent,
);

export const isIosWebViewWrapper = (baseTheme) => {
  if (isEqual(baseTheme, 'L4')) {
    return false;
  } else {
    return isIosWebView;
  }
};

export const isAndroidWebView = /(android)/i.test(navigator.userAgent);

export const isIOS = () => {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
      navigator.platform,
    ) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

export const isIPhone = () => [navigator.userAgent.platform, navigator.platform].includes('iPhone');

export const isAndroid = () => {
  const webappOS = detectWebAppOS();
  return webappOS === operatingPlatformsList.mobile.android;
};

export const isDesktopPlatform = () => {
  const deviceDetector = new DeviceDetector();
  const device = deviceDetector.parse(navigator && navigator.userAgent);
  const deviceType = get(device, 'device.type', '');

  return deviceType === 'desktop';
};

export const isMobilePlatform = () => {
  const deviceDetector = new DeviceDetector();
  const device = deviceDetector.parse(navigator && navigator.userAgent);
  const deviceType = get(device, 'device.type', '');

  return deviceType === 'smartphone' || isIPhone();
};

export const getDeviceType = () =>
  (isIOS() && 'ios') || (isAndroid() && 'android') || (isDesktopPlatform() && 'desktop') || '';

/**
 *
 * @param {String} key Key against which the target object is supposed to be matched
 * @param {String} value Value of the key to match provided in the 'key' argument
 * @returns {Object} Target object from onlinePaymentTypesConfig
 */
export const getOnlinePaymentTypeConfig = (key, value) => {
  const targetOnlinePaymentTypesConfigKey = Object.keys(onlinePaymentTypesConfig).find(
    (configKey) => onlinePaymentTypesConfig[configKey][key] === value,
  );

  return onlinePaymentTypesConfig[targetOnlinePaymentTypesConfigKey] || {};
};

export const updateStyles = (styleAttrProp, { bgImage, bgColor, fontSize, fontFamily, color }) => {
  if (bgImage) {
    styleAttrProp.backgroundImage = `url(${bgImage})`;
  }
  if (bgColor) {
    styleAttrProp.backgroundColor = bgColor;
  }
  if (fontSize) {
    styleAttrProp.fontSize = fontSize;
  }
  if (fontSize) {
    styleAttrProp.fontFamily = fontFamily;
  }
  if (color) {
    styleAttrProp.color = color;
  }
};

/**
 * Check if the two primitive-value arrays have the same values irrespective of their order in the two arrays
 *
 * @param {Array} array1
 * @param {Array} array2
 * @returns {Boolean}
 */
export const areSameArraysSansOrder = (array1, array2) =>
  array1.sort().join(',') === array2.sort().join(','); // sort arrays, convert to string and compare

/**
 * Return time salutation from the greeting time salutations Map
 *
 * @param {Date} time
 * @returns {String} salutation
 */
export const getTimeSalutation = (time) => {
  let salutation = greetingTimeSalutationsMap.get(timeOfDayStrings.night); // Return the value against the 'night' key from the greeting time salutations Map by default

  const greetingMatrix = Array.from(greetingTimeSalutationsMap.entries());
  greetingMatrix.forEach((greetingPairArray, index, originArray) => {
    const currentGreetingPair = greetingPairArray; // e.g. ['04:00', 'Hello']
    const currentGreetingHour = parseInt(currentGreetingPair[0].split(':')[0]); // e.g. 4
    const currentGreetingHourSalutation = currentGreetingPair[1]; // e.g. 'Hello'
    const nextGreetingPair =
      index + 1 < originArray.length ? originArray[index + 1] : greetingPairArray; // prevent value out of bounds by falling back to the current value in the iteration
    const nextGreetingHour = parseInt(nextGreetingPair[0].split(':')[0]);

    // If the military-time hour from the arg 'time' is between the range of the greeting military-time hours, then return the corresponding salutation in the greeting time salutations Map
    if (time.getHours() >= currentGreetingHour && time.getHours() < nextGreetingHour) {
      salutation = currentGreetingHourSalutation;
    }
  });

  return salutation;
};

/* intersperse: Return an array with the separator interspersed between
 * each element of the input array.
 *
 * > _([1,2,3]).intersperse(0)
 * [1,0,2,0,3]
 */
export const intersperse = (arr, sep) => {
  if (arr.length === 0) {
    return [];
  }

  return arr.slice(1).reduce((xs, x, i) => xs.concat([sep, x]), [arr[0]]);
};

/**
 * Match a route (URL pathname) from among a list of routes.
 * List of routes may contain URL wildcard pattern e.g. path/*.
 *
 * @param {Array} routeList
 * @param {String} targetRoute
 * @returns {Boolean} Positive match
 */
export const matchRoutePatternWithWildcardSupport = (routeList, targetRoute = '') => {
  if (routeList.includes(targetRoute)) return true;

  return routeList.some((route) => {
    if (!route) return false;

    if (route.includes('*')) {
      const subjectRoutePieces = route.split('/');
      let targetRoutePieces = targetRoute.split('/');

      if (subjectRoutePieces.length === targetRoutePieces.length) {
        return subjectRoutePieces.every(
          (subjectRoutePiece, index) =>
            subjectRoutePiece === '*' || subjectRoutePiece === targetRoutePieces[index],
        );
      } else {
        const index = subjectRoutePieces.findIndex((piece) => piece === '*');
        const includedPieces = subjectRoutePieces.slice(0, index);
        if (includedPieces.length === 0) return false;
        return includedPieces.every((piece) => targetRoutePieces.includes(piece));
      }
    }

    return false;
  });
};

export const handleShareUrl = ({ shareText, shareUrl }) => {
  // check for android web view as  navigator.share doesn't work there
  if (!isIosWebView) {
    window.open(shareUrl, '_blank');
  } else {
    if (navigator.share !== undefined) {
      navigator.share({ text: shareText, url: shareUrl }).then(
        () => console.log('Successful share'),
        (error) => console.log('Error sharing:', error),
      );
    }
  }
};

export const handleMailLink = ({ mailLink }) => {
  const mailUrl = `mailto:${mailLink}`;
  if (window.ReactNativeWebView) {
    const data = {
      shareType: { type: 'mail', url: mailUrl },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(data));
  } else {
    window.open(mailUrl, '_blank');
  }
};

export const handleTierBenefitsLink = (view, isHelpSupportPage) => {
  history.push(
    `${rewardsRoutes.containers.rewardProgramDetails}${view || isHelpSupportPage ? `?view=${rewardsRoutes.containers.account}` : ''
    }`,
    {
      tab: 1,
    },
  );
};

export const handleCallClick = ({ countryCode, phoneNumber }) => {
  if (window.ReactNativeWebView) {
    const data = {
      shareType: { type: 'mail', url: `tel:${phoneNumber}` },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(data));
  } else {
    window.open(`tel:${countryCode}${phoneNumber}`, '_self');
  }
};

export const replaceDOMContentWithLinks = (string, countryCode, view, isHelpSupportPage) => {
  const emailPattern = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
  const phonePattern =
    /(\+?1\s*[-\/\.]?\s*)?\(?[2-9]\d{2}\)?\s*[-\/\.]?\s*[2-9]\d{2}\s*[-\/\.]?\s*\d{4}/g;
  const linkPattern = /<a>(.*?)<\/a>/;
  const newParts = string.split(linkPattern);
  const parts = newParts
    .find((part) => part)
    .trim()
    .split(' ');

  if (newParts[1]) {
    parts.push(newParts[1]);
  }
  return parts.map((part, index) => {
    if (part.match(emailPattern)) {
      return (
        <a key={index} onClick={() => handleMailLink({ mailLink: part })}>
          {part}
        </a>
      );
    } else if (part.match(phonePattern)) {
      return (
        <a key={index} onClick={() => handleCallClick({ countryCode, phoneNumber: part })}>
          {part}
        </a>
      );
    } else if (isEqual(part, newParts[1])) {
      return (
        <div
          className="tier-benefits"
          key={index}
          onClick={() => handleTierBenefitsLink(view, isHelpSupportPage)}
        >
          {part}
        </div>
      );
    }
    return <div>{part}</div>;
  });
};

export const isNativeWebView = !!window.ReactNativeWebView;

export const matchAlphanumericCharacters = (text) => {
  var letters = /^[a-zA-Z0-9]+$/;
  return letters.test(text);
};

// FUDR-13758: native <input> / MUI fire (event) only; legacy handlers expect Semantic UI's
// onChange(event, { name, value }) or onChange(event, { checked }). Used in field wrappers.
export const wrapLegacyInputOnChange = (onChange, event, fieldName = '') => {
  if (!onChange) return;
  onChange(event, {
    name: get(event, 'target.name', fieldName),
    value: get(event, 'target.value', ''),
  });
};

export const wrapLegacyCheckboxOnChange = (onChange, event, checkedOrSecondArg, fieldName = '') => {
  if (!onChange) return;
  const checked =
    typeof checkedOrSecondArg === 'boolean'
      ? checkedOrSecondArg
      : get(checkedOrSecondArg, 'checked', get(event, 'target.checked', false));
  onChange(event, { name: get(event, 'target.name', fieldName), checked });
};

export const wrapLegacyRadioOnChange = (onChange, event, fieldName = '', fieldValue = '') => {
  if (!onChange) return;
  onChange(event, {
    name: get(event, 'target.name', fieldName),
    value: get(event, 'target.value', fieldValue),
  });
};

export const getComponentVersionProps = (themeData, componentName) => {
  const baseTheme = get(themeData, 'baseTheme', 'L1');
  const baseLayout = layoutConfiguration.find((layout) => isEqual(layout.version, baseTheme));
  const baseComponent = get(baseLayout, 'components', []).find((comp) =>
    isEqual(comp.name, componentName),
  );
  const baseComponentVersion = get(baseComponent, 'version');

  const themeComponent = get(themeData, 'changeComponents', []).find((comp) =>
    isEqual(comp.name, componentName),
  );
  if (isEqual(get(themeComponent, 'status'), 'remove')) {
    return { version: null };
  } else if (isEqual(get(themeComponent, 'status'), 'add')) {
    return {
      version: get(themeComponent, 'version', 'cv1'),
      style: get(themeComponent, 'style', {}),
    };
  }
  const themeComponentVersion = get(themeComponent, 'version', baseComponentVersion);

  const version = themeComponentVersion;
  return { ...themeComponent, version: version, style: get(themeComponent, 'style', {}) };
};

// A floating bottom-nav footer (bottom-nav-bar cv2/cv3) overlays the page
// content instead of sitting below it. When present, bottom-anchored menu-page
// popovers (e.g. the view-cart popover) must stay above the footer instead of
// dropping to the very bottom and hiding behind it.
export const isFloatingBottomNavBar = (themeData) => {
  const version = get(getComponentVersionProps(themeData, 'bottom-nav-bar'), 'version', '');
  return isEqual(version, 'cv2') || isEqual(version, 'cv3');
};

// L6 glass theme (page-header cv3) keeps the native OS status bar inactive
// (transparent) so the webview renders under it. The native app injects the
// status-bar height as the `--safe-area-inset-top` CSS var; we consume it to pad
// the fixed headers and content down below the status bar (see the
// `safe-area-top-inset` container class).
export const isGlassPageHeader = (themeData) =>
  isEqual(get(getComponentVersionProps(themeData, 'page-header'), 'version', ''), 'cv3');

export const getMaterialElementsVersion = (themeData) =>
  get(getComponentVersionProps(themeData, 'material-elements'), 'version');

export const getMaterialElementVersionFlags = (themeData) => {
  const materialElementsVersion = getMaterialElementsVersion(themeData);
  return {
    materialElementV2: isEqual(materialElementsVersion, 'cv2'),
    materialElementV3: isEqual(materialElementsVersion, 'cv3'),
    materialElementV4: isEqual(materialElementsVersion, 'cv4'),
  };
};

export const isUUID = (value) => {
  const uuidPattern =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return uuidPattern.test(value);
};

export const compareVersions = (version1, version2) => {
  const v1 = version1.split('.').map(Number);
  const v2 = version2.split('.').map(Number);

  const maxLength = Math.max(v1.length, v2.length);

  for (let i = 0; i < maxLength; i++) {
    const num1 = i < v1.length ? v1[i] : 0;
    const num2 = i < v2.length ? v2[i] : 0;

    if (num1 < num2) {
      return -1;
    } else if (num1 > num2) {
      return 1;
    }
  }
  // if versions are same
  return 0;
};

export const handleLinkUrl = ({ linkUrl, context }) => {
  if (isNativeWebView) {
    const data = {
      shareType: { type: 'link-url', url: linkUrl, context },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(data));
  } else {
    window.open(linkUrl, '_blank');
  }
};

export const buildApiUrl = (base_url, params_dict) => {
  if (!base_url.includes('&') && !base_url.endsWith('?')) {
    base_url += '?'; // Adding a question mark if missing
  } else if (!base_url.endsWith('&')) {
    base_url += '&';
  }
  const query_params = [];
  for (const [key, value] of Object.entries(params_dict)) {
    query_params.push(`${key}=${value}`);
  }
  return base_url + query_params.join('&');
};

export const calculateImageHeightWidth = (imageWidth, imageHeight) => {
  if (imageWidth < imageHeight || imageWidth <= imageHeight * 2) {
    return { height: '5.71em', width: 'auto' };
  } else if (imageWidth > imageHeight * 2 && imageWidth <= imageHeight * 3) {
    return { height: '4.57em', width: 'auto' };
  } else if (imageWidth > imageHeight * 3 && imageWidth <= imageHeight * 5) {
    return { height: '4em', width: 'auto' };
  } else if (imageWidth > imageHeight * 5 && imageWidth <= imageHeight * 7) {
    return { height: '3.42em', width: 'auto' };
  } else if (imageWidth === imageHeight) {
    return { height: '8em', width: '8em' };
  } else {
    return { height: 'auto', width: '100%' };
  }
};

export const calculateImageHeightWidthLoginPage = (imageWidth, imageHeight) => {
  if (imageWidth < imageHeight || imageWidth <= imageHeight * 2) {
    return { height: '8.57em', width: 'auto' };
  } else if (imageWidth > imageHeight * 2 && imageWidth <= imageHeight * 3) {
    return { height: '5.71em', width: 'auto' };
  } else if (imageWidth > imageHeight * 3 && imageWidth <= imageHeight * 4) {
    return { height: '5.14em', width: 'auto' };
  } else if (imageWidth > imageHeight * 4 && imageWidth <= imageHeight * 5) {
    return { height: '4em', width: 'auto' };
  } else if (imageWidth > imageHeight * 5 && imageWidth <= imageHeight * 7) {
    return { height: '3.42em', width: 'auto' };
  } else if (imageWidth === imageHeight) {
    return { height: '8.57em', width: '8.57em' };
  } else {
    return { height: 'auto', width: '100%' };
  }
};

export const formatAmount = (amount) => Number(amount).toLocaleString('en-IN') || amount;

export const englishNumbers = [
  'First',
  'Second',
  'Third',
  'Fourth',
  'Fifth',
  'Sixth',
  'Seventh',
  'Eighth',
  'Ninth',
  'Tenth',
];

// appStartup (/restaurant-service/appStartup) → brandResource.resource is the canonical brand CONFIG.
export const getBrandResource = (state, key, fallback) => {
  const fromAppStartup = get(state, `common.appStartupInfo.brandResource.resource.${key}`);
  if (fromAppStartup !== undefined) {
    return fromAppStartup;
  }
  const fromResources = get(state, `common.resources.${key}`);
  if (fromResources !== undefined) {
    return fromResources;
  }
  return fallback;
};

export const isMembershipPassDeal = (offer = {}) => {
  if (!offer) return false;
  if (offer.dealSubCategory === 'MEMBERSHIP') return true;
  const category = offer.category || offer.dealCategory;
  const subCat = offer.subCategory;
  if (category === 'PASS' && subCat === 'MEMBERSHIP') return true;
  return false;
};

export const isLegacyPassDeal = (offer = {}) => {
  if (!offer) return false;
  if (isMembershipPassDeal(offer)) return false;
  const category = offer.category || offer.dealCategory;
  const subCat = offer.subCategory;
  return category === 'PASS' || subCat === 'PASS';
};

/** Membership pass rewards render as individual deal cards, not grouped by dealSubCategory. */
export const getDealOfferGroupKey = (offer = {}) => {
  if (!offer) return undefined;
  if (isMembershipPassDeal(offer)) {
    return undefined;
  }
  return offer.dealSubCategory;
};

export const getPassOfferImageUrl = (offer = {}) => {
  if (!offer) return '';
  if (isMembershipPassDeal(offer)) {
    return (
      offer.entityImageUrl ||
      offer.dealImageUrl ||
      offer.couponImageUrl ||
      offer.couponImage ||
      offer.offerImage ||
      ''
    );
  }
  return offer.imageUrl || offer.offerImage || '';
};

export const handleDownloadAppClick = ({ appStartupInfo, history }) => {
  const customLink = get(appStartupInfo, 'brandResource.resource.downloadNudgeCustomLink', '');
  if (!isEmpty(customLink)) {
    window.location.href = customLink;
    return;
  }
  const deviceType = getDeviceType();
  /*To DO. Added for backward compatibility to remove dependenct from the reward config */
  const appLinksFromRewardRuleConfig = get(appStartupInfo, 'brand.additionalData.appLinks', {});
  const appLinksObj = get(
    appStartupInfo,
    'brandResource.resource.appLinks',
    appLinksFromRewardRuleConfig,
  );
  const appDownloadLink = appLinksObj[deviceType];
  if (!isEmpty(appDownloadLink)) {
    window.location.href = appDownloadLink;
    return;
  } else {
    history.push(`/${rewardsRoutes.rewards}/${rewardsRoutes.containers.getApp}`);
  }
};

export const getOutletDistance = ({
  latitude1,
  longitude1,
  latitude2,
  longitude2,
  outletDistanceDisplayThresoldInKm,
  nearbyOutletThresoldInMeter,
}) => {
  let outletDistance =
    checkDistanceBetweenCoordinates(
      {
        latitude1,
        longitude1,
        latitude2,
        longitude2,
      },
      false,
    ) || 0;
  if (outletDistance < outletDistanceDisplayThresoldInKm) {
    let outletDistanceInMeter = outletDistance * 1000;
    if (outletDistanceInMeter < nearbyOutletThresoldInMeter)
      return <FormattedMessage id="nearby" defaultMessage={resourcesConfig.nearby} />;
    outletDistanceInMeter = Math.round(outletDistanceInMeter);
    return `${outletDistanceInMeter} m`;
  } else {
    outletDistance = Math.round(outletDistance);
    return `${outletDistance} km`;
  }
};

export const renderOutletDistance = ({
  distance,
  outletDistanceDisplayThresoldInKm,
  nearbyOutletThresoldInMeter,
}) => {
  if (distance < outletDistanceDisplayThresoldInKm) {
    let outletDistanceInMeter = distance * 1000;
    if (outletDistanceInMeter < nearbyOutletThresoldInMeter)
      return <FormattedMessage id="nearby" defaultMessage={resourcesConfig.nearby} />;
    outletDistanceInMeter = Math.round(outletDistanceInMeter);
    return `${outletDistanceInMeter} m`;
  } else {
    distance = Math.round(distance);
    return `${distance} km`;
  }
};

export const fibonacciPolling = (maxTime) => {
  const series = [1, 1];
  let nextValue = 2;

  while (nextValue <= maxTime) {
    series.push(nextValue);
    const len = series.length;
    nextValue = series[len - 1] + series[len - 2];
  }

  return series;
};

export const isInstagramBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return userAgent.includes('Instagram');
};

export const safeDecode = (uri) => {
  try {
    return decodeURIComponent(uri);
  } catch (error) {
    console.error('safeDecode failed for:', uri, error);
    return uri;
  }
};

export const sanitizeValue = (value) => {
  return value && value.replace(/\s+/g, '');
};

let isAppInStartupPhase = true;
let isApiRequestOnHold = false;

export const setAppInStartupPhase = (value) => {
  isAppInStartupPhase = value;
};

export const getAppInStartupPhase = () => {
  return isAppInStartupPhase;
};

export const setIsApiRequestOnHold = (value) => {
  isApiRequestOnHold = value;
};

export const getIsApiRequestOnHold = () => {
  return isApiRequestOnHold;
};

export const getEnableProactiveTokenRefresh = () => {
  if (isNativeWebView) {
    const nativeFlag = get(window, 'nativeAppConfig.enableProactiveTokenRefresh');
    if (nativeFlag !== undefined && nativeFlag !== null) {
      return nativeFlag === true || String(nativeFlag) === 'true';
    }
  }
  return enableProactiveTokenRefresh;
};

const decodeJwtPayload = (token) => {
  const parts = String(token || '').split('.');
  if (parts.length !== 3) return null;
  try {
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) {
      base64 += '=';
    }
    return JSON.parse(atob(base64));
  } catch (error) {
    return null;
  }
};

export const getJWTExpiryTimeMs = (token) => {
  const payload = decodeJwtPayload(token);
  const expSeconds = get(payload, 'exp', null);
  if (expSeconds == null) return null;
  const expMs = Number(expSeconds) * 1000;
  return Number.isFinite(expMs) ? expMs : null;
};

export const getJWTRemainingTimeMs = (token, nowMs = Date.now()) => {
  const expMs = getJWTExpiryTimeMs(token);
  if (expMs == null) return null;
  return expMs - nowMs;
};

export const isJWTTokenExpiringSoon = (token, bufferMinutes, nowMs = Date.now()) => {
  const remainingMs = getJWTRemainingTimeMs(token, nowMs);
  if (remainingMs === null) return false;
  const bufferMs = Math.max(0, Number(bufferMinutes) || 0) * 60 * 1000;
  return remainingMs <= bufferMs;
};

export const isJWTTokenExpired = (token) => {
  // If no token is provided, treat it as invalid/expired for auth checks.
  if (!token) {
    return true;
  }

  try {
    const remainingMs = getJWTRemainingTimeMs(token);
    if (remainingMs === null) return false; // No expiry claim, treat as valid
    return remainingMs <= 0;
  } catch (error) {
    // If any error occurs during token parsing, treat as expired
    return true;
  }
};

export const notifyNativeSessionExpired = () => {
  if (!isNativeWebView) return false;
  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      eventType: { type: nativeEventType.sessionExpired },
    }),
  );
  return true;
};

export const notifyNativeAccessDenied = () => {
  if (!isNativeWebView) return false;
  window.ReactNativeWebView.postMessage(
    JSON.stringify({
      eventType: { type: nativeEventType.accessDenied },
    }),
  );
  return true;
};

/**
 * Determine if an error represents an authentication (401) or access denied (403) error.
 * Matches both numeric HTTP status and text codes.
 *
 * 401 codes: TOKEN_EXPIRED, INVALID_TOKEN, UNAUTHORIZED_ACTION
 * 403 codes: UNAUTHORIZED_ACTION, ACCESS_DENIED
 */
export const isAuth401Error = (errorLike = {}) => {
  // Coerce — statusCode may be number or string from API / interceptor.
  const numericStatus = Number(get(errorLike, 'statusCode', ''));
  const textCode = get(errorLike, 'code', ''); // TOKEN_EXPIRED, INVALID_TOKEN, UNAUTHORIZED_ACTION

  // 401 errors: status must be 401 AND code must be one of TOKEN_EXPIRED, INVALID_TOKEN, UNAUTHORIZED_ACTION
  if (numericStatus === 401) {
    return (
      textCode === serverErrorCodes.invalidToken ||
      textCode === serverErrorCodes.tokenExpired ||
      textCode === serverErrorCodes.unauthorizedAction
    );
  }

  return false;
};

/**
 * Determine if an error represents an access denied (403) error.
 * Matches both numeric HTTP status and text codes.
 *
 * 403 codes: UNAUTHORIZED_ACTION, ACCESS_DENIED
 */
export const isAccessDenied403Error = (errorLike = {}) => {
  const numericStatus = get(errorLike, 'statusCode', ''); // 403
  const textCode = get(errorLike, 'code', ''); // UNAUTHORIZED_ACTION, ACCESS_DENIED

  // 403 errors: status must be 403 AND code must be one of UNAUTHORIZED_ACTION, ACCESS_DENIED
  if (numericStatus === 403) {
    return (
      textCode === serverErrorCodes.unauthorizedAction || textCode === serverErrorCodes.accessDenied
    );
  }

  return false;
};

export const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    const maxWaitMs = 10000;
    if (window.Razorpay) {
      resolve();
      return;
    }
    // Avoid injecting the SDK multiple times across different payment screens.
    const existing = document.querySelector('script[data-razorpay-sdk="checkout"]');
    if (existing) {
      const status = existing.dataset.razorpaySdkStatus;

      // If the tag claims "loaded" but the global isn't present, execution was likely blocked.
      // Waiting can never succeed in that scenario, so remove and retry immediately.
      if (status === 'loaded' && !window.Razorpay) {
        existing.remove();
        loadRazorpayScript().then(resolve).catch(reject);
        return;
      }

      // If a previous injection failed, remove the stale tag and retry once.
      if (status === 'error') {
        existing.remove();
        loadRazorpayScript().then(resolve).catch(reject);
        return;
      }

      // If a previous injection is in-flight, wait for the global to appear.
      const start = Date.now();
      const waitForGlobal = () => {
        if (window.Razorpay) {
          resolve();
          return;
        }
        if (Date.now() - start > maxWaitMs) {
          reject(new Error('Timed out waiting for Razorpay SDK to initialise'));
          return;
        }
        setTimeout(waitForGlobal, 50);
      };
      waitForGlobal();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.defer = true;
    script.dataset.razorpaySdk = 'checkout';
    script.dataset.razorpaySdkStatus = 'loading';
    script.onload = () => {
      // Some blockers allow the download but prevent execution; ensure the global exists.
      if (window.Razorpay) {
        script.dataset.razorpaySdkStatus = 'loaded';
        resolve();
      } else {
        // Treat as a failed load so subsequent attempts can immediately retry.
        script.dataset.razorpaySdkStatus = 'error';
        reject(new Error('Razorpay SDK loaded but window.Razorpay is unavailable'));
      }
    };
    script.onerror = () => {
      script.dataset.razorpaySdkStatus = 'error';
      reject(new Error('Failed to load Razorpay SDK'));
    };
    document.head.appendChild(script);
  });

export const openRazorpayCheckout = async ({
  keyId,
  orderId,
  amount,
  brandName,
  prefill,
  onSuccess,
  onDismiss,
  onFailed,
}) => {
  if (!keyId || !orderId) {
    throw new Error('Missing Razorpay keyId or orderId');
  }

  await loadRazorpayScript();
  if (!window.Razorpay) {
    throw new Error('Razorpay SDK unavailable');
  }

  const resolvedPrefill =
    prefill && typeof prefill === 'object' && Object.keys(prefill).length ? prefill : null;

  // While the Razorpay modal is open, the browser back button can otherwise navigate to a
  // previous history entry (sometimes an old redirect-gateway URL). We push a dummy state
  // and intercept `popstate` to treat back as "dismiss" and keep the user on the same page.
  let popstateHandler = null;
  let didNotifyDismiss = false;
  let didNotifySuccess = false;
  const cleanupBackGuard = () => {
    if (popstateHandler) {
      try {
        window.removeEventListener('popstate', popstateHandler);
      } catch (e) {
        // ignore
      }
      popstateHandler = null;
    }
  };
  const safeDismiss = () => {
    // If success was already handled, ignore dismiss events (Razorpay may auto-close and call ondismiss).
    if (didNotifyDismiss || didNotifySuccess) return;
    didNotifyDismiss = true;
    cleanupBackGuard();
    onDismiss && onDismiss();
  };

  const rzp = new window.Razorpay({
    key: keyId,
    order_id: orderId,
    name: brandName,
    ...(resolvedPrefill ? { prefill: resolvedPrefill } : {}),
    handler: (response) => {
      // Always surface success if Razorpay reports it, even if the user previously pressed
      // back (which we treat as a local dismiss). Suppressing `onSuccess` can lead to a
      // successful server-side payment but no verification polling in the host app.
      if (didNotifySuccess) return;
      didNotifySuccess = true;
      cleanupBackGuard();
      onSuccess && onSuccess(response);
    },
    modal: {
      ondismiss: () => {
        safeDismiss();
      },
      // Prevent accidental dismissal via Escape key on desktop.
      escape: false,
    },
  });

  if (onFailed) {
    rzp.on('payment.failed', (response) => {
      // Razorpay can emit `payment.failed` while keeping the modal open for retry.
      // Do NOT remove the back-button guard here; only success or actual dismissal should clean it up.
      onFailed(response);
    });
  }

  // Preserve the current History state (React Router stores location state here).
  // The dummy entry must keep that state to avoid a "dead" back press after modal close.
  const baseHistoryState = (() => {
    try {
      return window.history.state;
    } catch (e) {
      return null;
    }
  })();
  try {
    window.history.pushState(
      { ...(baseHistoryState && typeof baseHistoryState === 'object' ? baseHistoryState : {}), __fudrRazorpayModal: true },
      '',
      window.location.href,
    );
  } catch (e) {
    // ignore
  }
  popstateHandler = () => {
    // Immediately re-add the current URL entry so the user stays on this route,
    // then close the modal and surface "Payment Failed" in the host page.
    try {
      const currentState = (() => {
        try {
          return window.history.state;
        } catch (e) {
          return null;
        }
      })();
      window.history.pushState(
        { ...(currentState && typeof currentState === 'object' ? currentState : {}), __fudrRazorpayModal: true },
        '',
        window.location.href,
      );
    } catch (e) {
      // ignore
    }
    try {
      rzp.close();
    } catch (e) {
      // ignore
    }
    // `rzp.close()` may trigger Razorpay's `modal.ondismiss` (which calls `safeDismiss()`).
    // Call `safeDismiss()` here as well so back-navigation still dismisses even if Razorpay
    // doesn't emit `ondismiss` for some reason. The guard makes this exactly-once.
    safeDismiss();
  };
  try {
    window.addEventListener('popstate', popstateHandler);
  } catch (e) {
    popstateHandler = null;
  }

  rzp.open();
  return rzp;
};

/**
 * Best-effort signal for back/forward navigation.
 *
 * Why: some environments (esp. mobile WebViews) don't reliably set React-Router
 * `history.action = 'POP'`, so we also consult Navigation Timing.
 */
export const isBackForwardNavigation = () => {
  try {
    const navEntries =
      performance && performance.getEntriesByType ? performance.getEntriesByType('navigation') : [];
    if (navEntries && navEntries.length > 0) {
      return navEntries[0].type === 'back_forward';
    }
    // Fallback for older browsers: 2 === back/forward
    // eslint-disable-next-line no-restricted-globals
    return performance && performance.navigation && performance.navigation.type === 2;
  } catch (e) {
    return false;
  }
};

/**
 * Shared initiation guard used across payment flows.
 * Prevents re-initiating payment on POP/back-forward navigation (avoids duplicate gateway orders).
 */
export const getShouldApplyInitiationGuard = (historyLike) => {
  const historyAction = get(historyLike, 'action', '');
  const hasHistoryAction = typeof historyAction === 'string' && historyAction.length > 0;
  const isBrowserHistoryPop = isEqual(historyAction, 'POP');

  // React Router's `history.action` reflects *the current navigation* (PUSH/REPLACE/POP).
  // Navigation Timing reflects the *initial page load* and does not update for SPA navigations.
  // So: if we have a usable `history.action`, trust it; only fall back to performance timing when we don't.
  return hasHistoryAction ? isBrowserHistoryPop : isBackForwardNavigation();
};

const COORD_COMPARE_DECIMALS = 6;

const roundCoordinate = (value) => {
  if (value === null || value === undefined) return NaN;
  if (typeof value === 'boolean' || Array.isArray(value) || typeof value === 'object') return NaN;

  let num;
  if (typeof value === 'number') {
    num = value;
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return NaN;
    num = Number(trimmed);
  } else {
    return NaN;
  }

  if (!Number.isFinite(num)) return NaN;
  const factor = 10 ** COORD_COMPARE_DECIMALS;
  return Math.round(num * factor) / factor;
};

/** Compare lat/lng from APIs that may return numbers or strings (e.g. 12.34 vs "12.34"). */
export const areCoordinatesEqual = (latA, lngA, latB, lngB) => {
  const lat1 = roundCoordinate(latA);
  const lat2 = roundCoordinate(latB);
  const lng1 = roundCoordinate(lngA);
  const lng2 = roundCoordinate(lngB);
  return !Number.isNaN(lat1) && !Number.isNaN(lat2) && lat1 === lat2 && lng1 === lng2;
};

const getPassViewMark = orderingRoutes.containers.sharedPassListView;

export const parseViewSegments = (view) =>
  String(view || '')
    .split(',')
    .map((segment) => segment.trim())
    .filter(Boolean);

export const isSharedPassListEntry = (history, view) =>
  get(history, 'location.state.isSharedPassListEntry', false) ||
  parseViewSegments(view).includes(getPassViewMark);

/** Catalog pass from pass list / get-pass link — not bought membership detail. */
export const isPassCatalogDetailView = (view) => {
  const segments = parseViewSegments(view);
  return (
    segments.includes(rewardsRoutes.containers.vipPassList) &&
    !segments.includes(rewardsRoutes.containers.payment) &&
    !segments.includes('fromPasses') &&
    !segments.includes('history') &&
    !segments.includes('detail') &&
    !segments.includes(rewardsRoutes.containers.vipPassDetail)
  );
};

export const buildGetPassListUrl = (loyaltyBase, viewQuerySuffix = '') =>
  `${loyaltyBase}/${orderingRoutes.containers.vipPassListGetPass}${viewQuerySuffix}`;

export const buildGetPassPaymentView = () =>
  `${getPassViewMark},${rewardsRoutes.containers.vipPassList},${rewardsRoutes.containers.payment}`;
